/* eslint-disable simple-import-sort/imports */
import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import {
  ADVANCED_FILTER_REDUCER_PATH,
  advancedFiltersReducer,
} from '@repo/advanced-filters';
import * as Sentry from '@sentry/react';
import {
  AUTOMATIONS_REDUCER_PATH,
  automationsReducer,
} from '@repo/automations';
import {
  CHAT_REDUCER_PATH,
  chatReducers,
} from '@repo/chat';
import {
  EMAIL_REDUCER_PATH,
  emailReducer,
} from '@repo/email';
import { getEnv, opinionatedAutoBatch } from '@repo/utils';

// services
import baseService, { baseServiceWithoutRefresh } from '@app/services/baseService';

// slices
import { configDetailsReducers } from '@repo/config-services';
import { indexedDBService } from '@repo/services';
import { leadDetailReducers } from './leadDetail';
import { manageLeadReducers, createEntityStorageMiddleware } from './manageLeads';
import { manageTasksReducers } from './manageTasks';
import { processFormsReducers } from './processForms';
import { activitiesReducers } from './activities';
import { permissionsReducers } from './permissions';
import { manageListsReducers } from './manageLists';
import { createListDetailStorageMiddleware, listDetailReducers } from './listDetail';
import { tableReducers } from './table';
import smartViewsReducers from './smartViews/smartViewsSlice';
import { settingsReducers } from './settings';

const sentryReduxEnhancer = Sentry.createReduxEnhancer({});

const indexedDBMiddleware = () => (next: any) => (action: any) => {
  const checkCacheSave = async () => {
    // Handle successful query responses
    if (action.type.endsWith('/executeQuery/fulfilled')) {
      const extraOptions = action.meta?.arg?.originalArgs;
      const cacheKey = extraOptions?.cacheKey;
      const cacheLifetime = extraOptions?.cacheLifetime;

      if (cacheKey && extraOptions?.cache !== false) {
        try {
          // Store the raw response data
          await indexedDBService.set(cacheKey, action.payload, cacheLifetime);
        } catch (error) {
          Sentry.captureMessage('Failed to cache data in indexDb', {
            level: 'info',
            extra: {
              error,
              cacheKey,
              endpointName: action.meta?.arg?.endpointName,
            },
          });
        }
      }
    }
  };

  checkCacheSave();

  return next(action);
};

export const store = configureStore({
  reducer: {
    leadDetail: leadDetailReducers,
    listDetail: listDetailReducers,
    manageLeads: manageLeadReducers,
    manageLists: manageListsReducers,
    manageTasks: manageTasksReducers,
    processForms: processFormsReducers,
    configDetails: configDetailsReducers,
    permissions: permissionsReducers,
    activities: activitiesReducers,
    table: tableReducers,
    smartViews: smartViewsReducers,
    settings: settingsReducers,
    [ADVANCED_FILTER_REDUCER_PATH]: advancedFiltersReducer,
    [EMAIL_REDUCER_PATH]: emailReducer,
    [CHAT_REDUCER_PATH]: chatReducers,
    [AUTOMATIONS_REDUCER_PATH]: automationsReducer,
    [baseService.reducerPath]: baseService.reducer,
    [baseServiceWithoutRefresh.reducerPath]: baseServiceWithoutRefresh.reducer,
  },
  middleware: (getDefaultMiddleware) => getDefaultMiddleware({
    serializableCheck: false,
  })
    .concat(baseService.middleware)
    .concat(baseServiceWithoutRefresh.middleware)
    .concat(indexedDBMiddleware)
    .concat(createEntityStorageMiddleware())
    .concat(createListDetailStorageMiddleware()),
  enhancers: (getDefaultEnhancers) => getDefaultEnhancers(
    { autoBatch: opinionatedAutoBatch },
  ).concat(sentryReduxEnhancer),
  devTools: getEnv('MODE') !== 'production',
});

setupListeners(store.dispatch);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
